<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue';
import {
  usePromptLibraryStore,
  type MetaCategoryItem,
  type CategoryItem,
} from '@/stores/prompt_library';
import { useRouter } from 'vue-router';
import { usePromptCollectionsStore } from '@/stores/promptCollectionsStore';
import { useAuthStore } from '@/stores/auth';
import {
  type PromptCreate,
  type PromptResponse,
  updateLangfuseCollectionByPromptCollection,
} from '@/api';
import { logger } from '@/utils/logger';
import Layout from '@/components/design/layout/Layout.vue';
import { navLabels } from '@/components/design/domain/navigation/navLabels';
import GaiaHeader from '@/components/design/base/GaiaHeader.vue';
import { gaiaContentWidth } from '@/components/design/themes/tokens';
import GaiaSelect from '@/components/design/base/selects/GaiaSelect.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

// Store and router access
const store = usePromptLibraryStore();
const collectionsStore = usePromptCollectionsStore();
const authStore = useAuthStore();
const router = useRouter();
const message = useGaiaToast();

// Local state
const activeMetaCategory = ref<string | undefined>(undefined);
const loadingState = ref<'initializing' | 'initialized' | 'error'>('initializing');

// Import functionality
const showImportModal = ref<boolean>(false);
const selectedCategory = ref<string>('');
const importCollectionName = ref<string>('');
const parsedPrompts = ref<PromptCreate[]>([]);
const isImporting = ref<boolean>(false);

// Update functionality
const showUpdateModal = ref<boolean>(false);
const selectedUpdateCategory = ref<string>('');
const selectedUserCollection = ref<string | undefined>(undefined);
const selectedEnvironments = ref<('development' | 'staging' | 'production' | 'test')[]>([
  'development',
]);
const isUpdating = ref<boolean>(false);

// Computed properties
const metaCategories = computed<MetaCategoryItem[]>(() => {
  return store.metaCategories;
});

const filteredCategories = computed<CategoryItem[]>(() => {
  if (!activeMetaCategory.value) return [];

  const metaCategory = metaCategories.value.find(item => item.name === activeMetaCategory.value);
  return metaCategory ? metaCategory.categories : [];
});

// User collections for update functionality
const userCollections = computed(() => {
  return collectionsStore.collections.map(collection => ({
    label: collection.name,
    value: collection.uid,
  }));
});

// Environment options for update functionality
const environmentOptions = [
  { label: 'Development', value: 'development' },
  { label: 'Staging', value: 'staging' },
  { label: 'Production', value: 'production' },
  { label: 'Test', value: 'test' },
];

// Functions
// The full collection name addresses the detail view
function categoryRoute(categoryName: string): string {
  const fullName = `${activeMetaCategory.value} - ${categoryName}`;
  return `/prompt-library/${encodeURIComponent(fullName)}`;
}

function viewCategory(categoryName: string): void {
  if (!activeMetaCategory.value) return;
  void router.push(categoryRoute(categoryName));
}

/**
 * Reloads the prompt library cache
 * Only available to admin users
 */
async function handleReloadCache(): Promise<void> {
  try {
    await store.reloadCache();
    message.success('Prompt library cache reloaded successfully');
  } catch (error) {
    logger.error('Error reloading cache:', error);
    message.error('Failed to reload prompt library cache');
  }
}

// Import functions
function showImportForCategory(categoryName: string, event: Event): void {
  // Stop the click event from bubbling up and navigating to the detail view
  event.stopPropagation();

  if (!activeMetaCategory.value) return;

  selectedCategory.value = categoryName;
  const fullName = `${activeMetaCategory.value} - ${categoryName}`;
  importCollectionName.value = fullName;

  // Find the category and prepare prompts
  const metaCategory = metaCategories.value.find(item => item.name === activeMetaCategory.value);
  if (metaCategory) {
    const category = metaCategory.categories.find(item => item.name === categoryName);
    if (category) {
      preparePromptsForImport(category.prompts);
      showImportModal.value = true;
    }
  }
}

function preparePromptsForImport(prompts: PromptResponse[]): void {
  parsedPrompts.value = [];

  for (const prompt of prompts) {
    if (!prompt.name || !prompt.prompt) continue;

    const responseType = prompt.response_type;

    parsedPrompts.value.push({
      name: prompt.name,
      prompt: prompt.prompt,
      response_type: responseType,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      evaluation_prompt: prompt.evaluation_prompt || null,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      evaluation_response_type: prompt.evaluation_response_type || null,
      // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must normalise to null
      suggestion_prompt: prompt.suggestion_prompt || null,
      suggestion_example_data: prompt.suggestion_example_data ?? null,
      suggestion_tool_selection: prompt.suggestion_tool_selection ?? null,
    });
  }
}

async function createCollectionWithPrompts(): Promise<void> {
  if (!authStore.userPermissions.is_org_admin) {
    message.error('You do not have permission to create prompts.');
    return;
  }
  if (!importCollectionName.value.trim()) {
    message.error('Collection name is required');
    return;
  }

  if (parsedPrompts.value.length === 0) {
    message.error('No prompts to import');
    return;
  }

  isImporting.value = true;

  try {
    // Create the collection
    const newCollection = await collectionsStore.createCollection(
      importCollectionName.value.trim()
    );
    if (!newCollection) {
      message.error('Failed to create collection');
      return;
    }

    // Add each prompt to the collection
    let successCount = 0;
    for (const prompt of parsedPrompts.value) {
      try {
        await collectionsStore.addPrompt(newCollection.uid, prompt);
        successCount++;
      } catch (error) {
        logger.error(`Error adding prompt "${prompt.name}":`, error);
      }
    }

    message.success(
      `Created collection with ${successCount} of ${parsedPrompts.value.length} prompts`
    );

    // Close modal and reset
    showImportModal.value = false;
    importCollectionName.value = '';
    parsedPrompts.value = [];
    selectedCategory.value = '';

    // Navigate to the collections view
    await router.push('/prompt-collections');
  } catch (error) {
    logger.error('Error creating collection with prompts:', error);
    message.error('Failed to create collection with prompts');
  } finally {
    isImporting.value = false;
  }
}

// Update functions
function showUpdateForCategory(categoryName: string, event: Event): void {
  // Stop the click event from bubbling up and navigating to the detail view
  event.stopPropagation();

  if (!activeMetaCategory.value) return;

  selectedUpdateCategory.value = categoryName;
  selectedUserCollection.value = undefined;
  selectedEnvironments.value = ['development']; // Default to development
  showUpdateModal.value = true;
}

async function updateLangfuseCollection(): Promise<void> {
  if (!selectedUserCollection.value) {
    message.error('Please select a collection to update from');
    return;
  }

  if (!selectedUpdateCategory.value || !activeMetaCategory.value) {
    message.error('Invalid category selection');
    return;
  }

  if (selectedEnvironments.value.length === 0) {
    message.error('Please select at least one environment');
    return;
  }

  isUpdating.value = true;

  try {
    await updateLangfuseCollectionByPromptCollection({
      path: { collection_uid: selectedUserCollection.value },
      body: selectedEnvironments.value,
    });

    message.success(
      `Library collection updated successfully for ${selectedEnvironments.value.join(', ')} environment(s)`
    );

    // Close modal and reset
    showUpdateModal.value = false;
    selectedUpdateCategory.value = '';
    selectedUserCollection.value = undefined;
    selectedEnvironments.value = ['development'];

    // Reload the prompt library to reflect changes
    await store.reloadCache();
  } catch (error) {
    logger.error('Error updating library collection:', error);
    message.error('Failed to update library collection');
  } finally {
    isUpdating.value = false;
  }
}

// Select first category when data is loaded
function selectFirstCategory(): void {
  if (metaCategories.value.length > 0 && !activeMetaCategory.value) {
    activeMetaCategory.value = metaCategories.value[0].name;
  }
}

// Watch for changes in initialization state
watch(
  () => store.initialized,
  newValue => {
    if (newValue) {
      // When initialization is complete, select the first category
      selectFirstCategory();
      loadingState.value = 'initialized';
    }
  }
);

// Watch for changes in meta categories
watch(
  metaCategories,
  newValue => {
    if (newValue.length > 0 && !activeMetaCategory.value) {
      selectFirstCategory();
    }
  },
  { immediate: true }
);

// Lifecycle hooks
onMounted(async (): Promise<void> => {
  try {
    loadingState.value = 'initializing';

    if (store.initialized) {
      // If already initialized, select first category
      selectFirstCategory();
      loadingState.value = 'initialized';
    } else {
      await store.initialize();
    }

    // Fetch user collections for update functionality
    await collectionsStore.fetchCollections();
  } catch (error) {
    logger.error('Error initializing prompt library view:', error);
    loadingState.value = 'error';
  }
});
</script>

<template>
  <Layout :content-max-width="gaiaContentWidth.table">
    <template #breadcrumb>
      <n-breadcrumb-item>{{ navLabels.repository }}</n-breadcrumb-item>
      <n-breadcrumb-item>{{ navLabels.gaiaPrompts }}</n-breadcrumb-item>
    </template>

    <n-space vertical>
      <!-- Page Header -->
      <div class="library-header-wrapper">
        <gaia-header :level="1" variant="display" size="screen" :style="{ margin: 0 }">
          {{ navLabels.gaiaPrompts }}
        </gaia-header>
        <!-- Admin Reload Cache Button -->
        <n-button
          v-if="authStore.userPermissions.is_org_admin"
          type="primary"
          :loading="store.isReloadingCache"
          :disabled="store.isLoading || store.isInitializing"
          @click="handleReloadCache"
        >
          <template #icon>
            <n-icon>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="1em"
                height="1em"
                fill="currentColor"
              >
                <path
                  d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0 0 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 0 0 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"
                />
              </svg>
            </n-icon>
          </template>
          Reload Cache
        </n-button>
      </div>

      <!-- Info text about the library -->
      <n-p class="library-info">
        This library is provided by GAIA for common use cases. Browse through the categories to find
        pre-made prompts that can help you accomplish various tasks more efficiently. You can import
        these prompts into your own collections using the import button.
      </n-p>

      <!-- Loading state -->
      <div v-if="store.isLoading || store.isInitializing" class="loading-container">
        <n-spin size="large" />
      </div>

      <!-- Error state -->
      <n-result
        v-else-if="store.error || loadingState === 'error'"
        status="error"
        title="Error Loading Prompt Library"
        :description="store.error || ''"
      >
        <template #footer>
          <n-button @click="store.initialize">Try Again</n-button>
        </template>
      </n-result>

      <!-- Main content -->
      <div v-else>
        <!-- Meta Categories Tabs -->
        <n-tabs v-model:value="activeMetaCategory" type="line" animated>
          <n-tab-pane
            v-for="category in metaCategories"
            :key="category.name"
            :name="category.name"
            :tab="category.name"
          />
        </n-tabs>

        <!-- Category Cards Grid -->
        <n-grid
          v-if="filteredCategories.length > 0"
          :cols="12"
          :x-gap="12"
          :y-gap="12"
          item-responsive
        >
          <n-grid-item v-for="category in filteredCategories" :key="category.name" :span="6">
            <n-card hoverable @click="viewCategory(category.name)">
              <template #header>
                <div class="card-header">
                  <router-link
                    :to="categoryRoute(category.name)"
                    class="card-title"
                    style="color: inherit; text-decoration: none"
                    @click.stop
                  >
                    {{ category.name }}
                  </router-link>
                  <div class="card-actions">
                    <n-tag type="primary" size="small">{{ category.prompts.length }} prompts</n-tag>
                    <n-button
                      size="small"
                      type="primary"
                      @click="showImportForCategory(category.name, $event)"
                    >
                      Import
                    </n-button>
                    <n-button
                      v-if="authStore.userPermissions.is_org_admin"
                      size="small"
                      type="warning"
                      @click="showUpdateForCategory(category.name, $event)"
                    >
                      Update
                    </n-button>
                  </div>
                </div>
              </template>
              <div class="card-content">
                <n-text depth="3">
                  {{
                    category.prompts.length > 0
                      ? `Includes ${category.prompts
                          .slice(0, 2)
                          .map(p => p.name.replace(/_/g, ' '))
                          .join(', ')}${category.prompts.length > 2 ? '...' : ''}`
                      : 'No prompts available'
                  }}
                </n-text>
              </div>
            </n-card>
          </n-grid-item>
        </n-grid>

        <!-- No categories found for the active meta category -->
        <n-empty v-else-if="activeMetaCategory" description="No categories found in this section" />

        <!-- No meta category selected -->
        <n-empty v-else description="Select a category above to view prompts" />
      </div>
    </n-space>

    <!-- Import Modal -->
    <n-modal v-model:show="showImportModal" style="width: 600px">
      <n-card title="Import Collection" :bordered="false">
        <n-space vertical>
          <n-form-item label="Collection Name" required>
            <gaia-input v-model:value="importCollectionName" placeholder="Enter collection name" />
          </n-form-item>

          <n-text type="info">
            This will import {{ parsedPrompts.length }} prompts from the "{{ selectedCategory }}"
            category to your collections.
          </n-text>
        </n-space>

        <template #footer>
          <n-space justify="end">
            <n-button @click="showImportModal = false">Cancel</n-button>
            <n-button
              type="primary"
              :disabled="
                !authStore.userPermissions.is_org_admin ||
                !importCollectionName.trim() ||
                parsedPrompts.length === 0
              "
              :loading="isImporting"
              @click="createCollectionWithPrompts"
            >
              Import
            </n-button>
          </n-space>
        </template>
      </n-card>
    </n-modal>

    <!-- Update Modal -->
    <n-modal v-model:show="showUpdateModal" style="width: 600px">
      <n-card title="Update Library Collection" :bordered="false">
        <n-space vertical>
          <n-alert type="warning" title="Warning" :show-icon="true">
            <n-text>
              This will update the library collection "{{ selectedUpdateCategory }}" for the
              selected environments. The changes will be visible to ALL users who import this
              collection. This action cannot be undone.
            </n-text>
          </n-alert>

          <n-form-item label="Select Your Collection to Update From" required>
            <gaia-select
              v-model:value="selectedUserCollection"
              placeholder="Choose a collection from your organization"
              :options="userCollections"
              filterable
              :loading="collectionsStore.isLoading"
              clearable
            />
          </n-form-item>

          <n-form-item label="Select Environments to Update" required>
            <gaia-select
              v-model:value="selectedEnvironments"
              placeholder="Choose environments to update"
              :options="environmentOptions"
              multiple
            />
          </n-form-item>

          <n-text type="info">
            This will replace the current library collection with prompts from your selected
            collection.
          </n-text>
        </n-space>

        <template #footer>
          <n-space justify="end">
            <n-button @click="showUpdateModal = false">Cancel</n-button>
            <n-button
              type="warning"
              :disabled="!selectedUserCollection"
              :loading="isUpdating"
              @click="updateLangfuseCollection"
            >
              Update Library
            </n-button>
          </n-space>
        </template>
      </n-card>
    </n-modal>
  </Layout>
</template>

<style scoped>
.loading-container {
  margin: 10px 0;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-text {
  display: block;
  text-align: center;
  margin-top: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-title {
  font-weight: 500;
  font-size: 16px;
}

.card-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.card-content {
  min-height: 40px;
}

.library-header-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.library-info {
  margin-top: 0;
  margin-bottom: 24px;
  color: var(--text-color-secondary);
}
</style>
